import type { ColumnDef } from "@tanstack/react-table";

import type { MetricKey, MetricType, PerfRow } from "../types";

import { ChevronRightIcon } from "../../Icons";
import { TableMobile } from "../../Table";
import { Typo } from "../../Typo";
import { fmtInt, fmtPct, fmtUsd, hasMetric, metricVal } from "../helpers";
import { usePerformanceReportTranslations } from "../hooks/usePerformanceReportTranslations";
import { usePerformanceReportContext } from "../providers/PerformanceReportProvider";
import { SourceCell } from "../table/SourceCell";

const fmtMetric = (value: number, type: MetricType): string => {
  if (type === "pct") return fmtPct(value);
  if (type === "usd") return fmtUsd(value);
  return fmtInt(value);
};

// TableMobile's non-expandable row wrapper hardcodes `rounded-lg border p-4`
// (packages/ui/src/Table/TableMobile.tsx) via string concatenation, not
// clsx/twMerge — `!important` always wins over a non-important declaration
// regardless of class order, so these overrides reliably strip the card
// styling down to a flush, divider-separated list.
// Every row keeps its bottom divider, including the last one (the prototype
// was updated to close the list off rather than leave the final row open).
const ROW_CONTAINER_CLASSNAME =
  "!my-0 !rounded-none !border-x-0 !border-t-0 !border-b !border-stroke-primary !p-0";

const ROW_COLUMNS: ColumnDef<PerfRow, unknown>[] = [
  { accessorKey: "sortName", id: "sortName" },
];

type MobileRowListProps = {
  rows: PerfRow[];
  onRowTap: (row: PerfRow) => void;
  /** 0-based current page index (tanstack `pageIndex`). */
  page: number;
  pageSize: number;
  /** Total matching rows across all pages. */
  totalCount: number;
  /** Called with the new 0-based page index. */
  onPageChange: (pageIndex: number) => void;
};

/**
 * Mobile row list — rows with the identity block and a single right-aligned
 * value for the active metric. The visible metric is driven by the shared sort
 * state (`MobileSortExportRow`), so sort and display are one control, not two.
 *
 * The column header lives in `MobileRowListHeader`, rendered by the page above
 * the loading/error/empty branch so it survives all four states.
 * The updated AC-6 (edited 2026-07 after the design-vs-AC conflict was ruled
 * on) confirms this "single selectable metric per row" model over the earlier
 * fixed 3-stat strip.
 *
 * Pagination is server-side: the API returns one page of `rows` plus a
 * `totalCount`, so we drive `TableMobile`'s built-in `enableServerSidePagination`
 * (the same footer `SubIBsTable`/`SharedRebateProgramsView` use) rather than
 * slicing client-side.
 */
export const MobileRowList = ({
  rows,
  onRowTap,
  page,
  pageSize,
  totalCount,
  onPageChange,
}: MobileRowListProps) => {
  const translations = usePerformanceReportTranslations();
  const { sorting } = usePerformanceReportContext();

  const metricKey = (sorting[0]?.id ?? "clicks") as MetricKey;
  const metricCol = translations.metricColumns.find((c) => c.key === metricKey);
  const metricType: MetricType = metricCol?.type ?? "int";

  return (
    <div className="flex flex-col">
      <TableMobile
        data={rows}
        columns={ROW_COLUMNS}
        showPagination
        enableServerSidePagination
        pageSize={pageSize}
        pageCount={Math.ceil((totalCount || 0) / (pageSize || 10))}
        totalCount={totalCount}
        state={{ pagination: { pageIndex: page, pageSize } }}
        onPaginationChange={(updater) => {
          const next =
            typeof updater === "function"
              ? updater({ pageIndex: page, pageSize })
              : updater;
          onPageChange(next.pageIndex);
        }}
        cellContainerClassName={ROW_CONTAINER_CLASSNAME}
        paginationSpacingClassName="h-4"
        filterFns={{} as never}
        cardBody={(row) => (
          <button
            type="button"
            onClick={() => onRowTap(row)}
            className="flex min-h-11 w-full items-center gap-2 py-3.5 text-start"
          >
            <div className="min-w-0 flex-1">
              <SourceCell row={row} isMobile />
            </div>
            <Typo
              element="span"
              type="labelSM"
              textColor="text-text-primary"
              className="shrink-0 tabular-nums"
            >
              {hasMetric(row, metricKey)
                ? fmtMetric(metricVal(row, metricKey), metricType)
                : "—"}
            </Typo>
            <ChevronRightIcon
              className="h-4 w-4 shrink-0 text-icon-tertiary"
              aria-hidden="true"
              focusable="false"
            />
          </button>
        )}
      />
    </div>
  );
};
